import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface ReportFilters {
  branchId: string;
  from: string;
  to: string;
  docType: '' | 'pos' | 'invoice';
  /** The toolbar's free-text search. */
  search: string;
  /**
   * The Filters popover's rows, already serialised as the JSON the API
   * expects. Kept as the wire string rather than a parsed array so it
   * drops straight into the query string and into react-query's key
   * without a custom serialiser.
   */
  filters: string;
  /** '' or 'true' → OR the rows; 'false' → AND them. */
  matchAny: string;
}

const EMPTY_FILTERS: ReportFilters = {
  branchId: '',
  from: '',
  to: '',
  docType: '',
  search: '',
  filters: '',
  matchAny: '',
};

/**
 * URL-synced so a report link (e.g. from Dashboard to Sales trend) carries the
 * same filter forward, and a reload doesn't reset it. Every report page
 * (Tasks 9-10 onward) uses this same hook, so filters stay consistent across
 * navigation between report pages.
 */
export function useReportFilters(): [ReportFilters, (next: Partial<ReportFilters>) => void] {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<ReportFilters>(
    () => ({
      branchId: params.get('branchId') ?? '',
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      docType: (params.get('docType') as ReportFilters['docType']) ?? '',
      search: params.get('search') ?? '',
      filters: params.get('filters') ?? '',
      matchAny: params.get('matchAny') ?? '',
    }),
    [params],
  );

  const update = (next: Partial<ReportFilters>) => {
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      const current: ReportFilters = {
        branchId: prev.get('branchId') ?? EMPTY_FILTERS.branchId,
        from: prev.get('from') ?? EMPTY_FILTERS.from,
        to: prev.get('to') ?? EMPTY_FILTERS.to,
        docType: (prev.get('docType') as ReportFilters['docType']) ?? EMPTY_FILTERS.docType,
        search: prev.get('search') ?? EMPTY_FILTERS.search,
        filters: prev.get('filters') ?? EMPTY_FILTERS.filters,
        matchAny: prev.get('matchAny') ?? EMPTY_FILTERS.matchAny,
      };
      for (const [key, value] of Object.entries({ ...current, ...next })) {
        if (value) merged.set(key, value);
        else merged.delete(key);
      }
      return merged;
    });
  };

  return [filters, update];
}
